import * as Metrics from 'metrics/Metrics';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import * as Logging from 'util/Logging';

/**
 * Redirect-oriented Google Drive OAuth popup.
 *
 * Unlike the callback-oriented flow (which runs OAuth in-browser via GIS and
 * exposes the access token on the DOM), this flow opens a Benchling backend
 * popup that performs the OAuth redirect dance with Google server-side. The
 * finish page hands the access token back to us via `window.opener.postMessage`
 * and then closes itself. The token is delivered in-memory only — it never
 * touches the DOM.
 *
 * Backend contract (PR #244721 / BNCH-158427):
 * - `GET /ext/tfi/browser-gdrive-oauth/start` returns a 302 to Google. We open
 *   it in a popup with `window.open` (NOT `fetch`).
 * - The finish page calls `window.opener.postMessage(payload, tenantOrigin)`
 *   then `window.close()`, where `payload` is either `{accessToken}` (success)
 *   or `{error: {severity, message}}` (failure).
 * - Target origin, CSRF `state`, `Sec-Fetch-Site`, `@logged_in`, and
 *   `Cache-Control`/`Referrer-Policy` are all enforced server-side. We still
 *   independently validate `event.source` and `event.origin` here as defense in
 *   depth — a malicious page must not be able to feed us a forged token.
 */

const START_URL = '/ext/tfi/browser-gdrive-oauth/start';
const POPUP_NAME = 'benchling-gdrive-oauth';
const POPUP_FEATURES = 'popup=yes,width=600,height=700';

// How often we poll `popup.closed` to detect the user dismissing the popup
// without completing OAuth (no `message` is posted in that case).
const POPUP_CLOSED_POLL_INTERVAL_MS = 500;

// Low-cardinality metrics. NO user data in names or tags.
const METRIC_POPUP_OPENED = 'notebook.gdrive_picker.redirect.oauth_popup.popup_opened';
const METRIC_POPUP_BLOCKED = 'notebook.gdrive_picker.redirect.oauth_popup.popup_blocked';
const METRIC_TOKEN_RECEIVED = 'notebook.gdrive_picker.redirect.oauth_popup.token_received';
const METRIC_MESSAGE_REJECTED = 'notebook.gdrive_picker.redirect.oauth_popup.message_rejected';
const METRIC_OAUTH_ERROR = 'notebook.gdrive_picker.redirect.oauth_popup.oauth_error';
// Low-cardinality, no user data; fired only when the user dismisses the popup
// before OAuth completes (never on the token-delivered path — see the `settled`
// guard in the close poll).
const METRIC_POPUP_CLOSED_BY_USER = 'notebook.gdrive_picker.redirect.oauth_popup.popup_closed_by_user';

/** Error severities the backend finish page can report. */
type OAuthErrorSeverity = 'WARN' | 'ERROR';

interface OAuthSuccessPayload {
  accessToken: string;
}

interface OAuthErrorPayload {
  error: {
    severity: OAuthErrorSeverity;
    message: string;
  };
}

/** Error thrown when the browser blocks the OAuth popup from opening. */
export class GDrivePopupBlockedError extends Error {
  constructor() {
    super('The Google Drive authorization popup was blocked by the browser.');
    this.name = 'GDrivePopupBlockedError';
  }
}

/** Error thrown when the user closes the popup before completing OAuth. */
export class GDrivePopupClosedError extends Error {
  constructor() {
    super('The Google Drive authorization popup was closed.');
    this.name = 'GDrivePopupClosedError';
  }
}

/** Error thrown when the backend finish page reports an OAuth failure. */
export class GDriveOAuthError extends Error {
  readonly severity: OAuthErrorSeverity;
  constructor(severity: OAuthErrorSeverity, message: string) {
    super(message);
    this.name = 'GDriveOAuthError';
    this.severity = severity;
  }
}

function isOAuthSuccessPayload(data: unknown): data is OAuthSuccessPayload {
  return (
    typeof data === 'object' &&
    data != null &&
    typeof (data as OAuthSuccessPayload).accessToken === 'string' &&
    (data as OAuthSuccessPayload).accessToken.length > 0
  );
}

function isOAuthErrorPayload(data: unknown): data is OAuthErrorPayload {
  if (typeof data !== 'object' || data == null) {
    return false;
  }
  const error = (data as OAuthErrorPayload).error;
  return (
    typeof error === 'object' &&
    error != null &&
    (error.severity === 'WARN' || error.severity === 'ERROR') &&
    typeof error.message === 'string'
  );
}

/** Maps a backend error severity to the corresponding toast type. */
function toastTypeForSeverity(severity: OAuthErrorSeverity): ToastType {
  return severity === 'ERROR' ? ToastType.DANGER : ToastType.WARNING;
}

/**
 * Opens the Benchling Google Drive OAuth popup and resolves with the access
 * token posted back by the finish page.
 *
 * Rejects with:
 * - `GDrivePopupBlockedError` if the browser blocked the popup,
 * - `GDrivePopupClosedError` if the user closed the popup before completing OAuth,
 * - `GDriveOAuthError` if the backend reported an OAuth failure (a toast is shown).
 *
 * Registers a single `message` listener and a `popup.closed` poll; both are
 * cleaned up on resolve and on reject so no listeners or intervals leak across
 * repeated authorization attempts.
 */
export function openGDriveOAuthPopup(): Promise<string> {
  const popup = window.open(START_URL, POPUP_NAME, POPUP_FEATURES);

  if (popup == null) {
    Metrics.count(METRIC_POPUP_BLOCKED);
    return Promise.reject(new GDrivePopupBlockedError());
  }

  Metrics.count(METRIC_POPUP_OPENED);

  return new Promise<string>((resolve, reject) => {
    // The expected origin of the finish page. The popup is same-origin with
    // Benchling (the backend performs the redirect), so a valid token message
    // must come from our own origin.
    const expectedOrigin = window.location.origin;

    let closedPollId: ReturnType<typeof setInterval> | undefined;
    // Once the promise has settled (resolved with a token or rejected), no later
    // event may change the outcome. This closes a race: the finish page posts the
    // token and then self-closes, so if the `popup.closed` poll's timer tick lands
    // before the `message` event is dispatched, the poll would otherwise observe
    // `popup.closed` and reject with GDrivePopupClosedError — dropping a VALID
    // token. Guarding the poll on `settled` makes token delivery always win.
    let settled = false;

    const cleanup = (): void => {
      window.removeEventListener('message', handleMessage);
      if (closedPollId !== undefined) {
        clearInterval(closedPollId);
        closedPollId = undefined;
      }
    };

    const resolveWith = (accessToken: string): void => {
      settled = true;
      cleanup();
      // The main page closes the popup itself rather than relying on the finish
      // page's self-close (same-origin popup, so window.close() is permitted).
      // This makes the flow self-sufficient regardless of backend template timing.
      if (!popup.closed) {
        popup.close();
      }
      resolve(accessToken);
    };

    const rejectWith = (err: Error): void => {
      settled = true;
      cleanup();
      // Mirror the success path: the main page closes the popup itself (same-origin)
      // rather than relying on the finish page's self-close. On a genuine user-close
      // the popup is already closed, so the guard makes that path a no-op.
      if (!popup.closed) {
        popup.close();
      }
      reject(err);
    };

    function handleMessage(event: MessageEvent): void {
      // Defense in depth: only trust messages from the popup we opened, on our
      // own origin. The backend already enforces target origin / CSRF / headers,
      // but we must not resolve on a token forged by any other frame.
      if (event.source !== popup) {
        Metrics.count(METRIC_MESSAGE_REJECTED, 1, {reason: 'bad_source'});
        return;
      }
      if (event.origin !== expectedOrigin) {
        Metrics.count(METRIC_MESSAGE_REJECTED, 1, {reason: 'bad_origin'});
        return;
      }

      const {data} = event;
      if (isOAuthSuccessPayload(data)) {
        Metrics.count(METRIC_TOKEN_RECEIVED);
        resolveWith(data.accessToken);
        return;
      }
      if (isOAuthErrorPayload(data)) {
        Metrics.count(METRIC_OAUTH_ERROR, 1, {severity: data.error.severity});
        ToastActions.pushToast({
          type: toastTypeForSeverity(data.error.severity),
          message: data.error.message,
        });
        // rejectWith closes the popup itself (same-origin), mirroring the success path.
        rejectWith(new GDriveOAuthError(data.error.severity, data.error.message));
        return;
      }

      // A message from the right source/origin but an unrecognized shape. Log
      // it (without user data) so a backend contract drift is visible, but do
      // not resolve or reject — keep waiting for a well-formed message.
      Metrics.count(METRIC_MESSAGE_REJECTED, 1, {reason: 'bad_shape'});
      Logging.warn('GDrive OAuth popup received a message with an unexpected shape', {
        extra: {dataType: typeof data},
      });
    }

    window.addEventListener('message', handleMessage);

    // Detects the user dismissing the popup without completing OAuth (no message
    // is ever posted in that case). On the happy path `handleMessage` settles the
    // promise first; the `settled` guard below then makes any subsequent
    // popup.closed observation a no-op, so this poll only rejects on a genuine
    // user-close.
    closedPollId = setInterval(() => {
      // Token delivery (or an error/toast) always wins: if the promise has
      // already settled, ignore a now-closed popup so we never turn a delivered
      // token into a spurious GDrivePopupClosedError.
      if (settled) {
        return;
      }
      if (popup.closed) {
        // A genuine user-close: no message was ever posted, so `settled` is still
        // false. Emit the low-cardinality metric before rejecting.
        Metrics.count(METRIC_POPUP_CLOSED_BY_USER);
        rejectWith(new GDrivePopupClosedError());
      }
    }, POPUP_CLOSED_POLL_INTERVAL_MS);
  });
}
